import { useEffect, useMemo, useState } from 'react';
import { Select, Card } from '../../../../shared/components';
import { ComparativosMultiSelect } from './comparativos/ComparativosMultiSelect';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { SummaryRow, SummaryDetailRow, ServiceType, TipoCuenta, MONTH_NAMES_ES } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const money = (v: number) => v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

/** Umbral para considerar un valor "sin información" (evita ruido por redondeos). */
const EPS = 0.5;


/**
 * Pestaña "Tabla Resumen" - equivalente a la tabla dinámica del Excel:
 * INGRESO/GASTOS por mes, filtrable por Tipo de Servicio y Cliente con
 * checklist de selección múltiple (igual comportamiento que el filtro de
 * la tabla dinámica), y con GASTOS/INGRESO expandibles ("+"/"−") para ver
 * el desglose por cuenta, calculado en vivo desde TB_Results_AccountDetail.
 */
export function ResultsSummaryTab() {
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [tiposServicio, setTiposServicio] = useState<string[]>([]);
  const [clientes, setClientes] = useState<string[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([]);
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [expanded, setExpanded] = useState<Partial<Record<TipoCuenta, boolean>>>({});
  const [detail, setDetail] = useState<Partial<Record<TipoCuenta, SummaryDetailRow[]>>>({});
  const [loadingDetail, setLoadingDetail] = useState<Partial<Record<TipoCuenta, boolean>>>({});

  useEffect(() => {
    Promise.all([repo.listServiceTypes(), repo.listDistinctClients()]).then(([st, clients]) => {
      setServiceTypeOptions(st.map((s: ServiceType) => s.nombre));
      setClientOptions(clients);
    });
  }, []);

  useEffect(() => {
    repo.getSummary({ anio, tiposServicio: tiposServicio.length > 0 ? tiposServicio : undefined, clientes: clientes.length > 0 ? clientes : undefined }).then(setRows);
    // Los filtros cambiaron: el detalle expandido que estaba cargado ya no es válido, se limpia para forzar recarga al volver a expandir.
    setDetail({});
  }, [anio, tiposServicio, clientes]);

  const toggleExpand = async (tipoCuenta: TipoCuenta) => {
    const next = !expanded[tipoCuenta];
    setExpanded((prev) => ({ ...prev, [tipoCuenta]: next }));
    if (next && !detail[tipoCuenta]) {
      setLoadingDetail((prev) => ({ ...prev, [tipoCuenta]: true }));
      const rowsDetail = await repo.getSummaryDetail({ anio, tipoCuenta, tiposServicio: tiposServicio.length > 0 ? tiposServicio : undefined, clientes: clientes.length > 0 ? clientes : undefined });
      setDetail((prev) => ({ ...prev, [tipoCuenta]: rowsDetail }));
      setLoadingDetail((prev) => ({ ...prev, [tipoCuenta]: false }));
    }
  };

  const ingreso = rows.find((r) => r.tipoCuenta === 'INGRESO');
  const gastos = rows.find((r) => r.tipoCuenta === 'GASTOS');
  // Se usa Math.abs() a propósito: GASTOS siempre debe restar, sin importar si quedó
  // guardado en positivo (import de una sola hoja, formato actual) o en negativo
  // (import viejo de dos hojas, formato heredado de "datos tabla").
  const totalByMonthAll = MONTH_NAMES_ES.map((m) => (ingreso?.valuesByMonth[m] ?? 0) - Math.abs(gastos?.valuesByMonth[m] ?? 0));

  // Excel-style: no mostrar columnas de meses sin ninguna información cargada
  // (ni INGRESO ni GASTOS). Si por algún motivo no queda ningún mes visible
  // (ej. año totalmente vacío) se muestran los 12 para no dejar la tabla en blanco.
  const visibleMonthIdx = useMemo(() => {
    const idx = MONTH_NAMES_ES.map((m, i) => i).filter((i) => {
      const m = MONTH_NAMES_ES[i];
      const hasIngreso = Math.abs(ingreso?.valuesByMonth[m] ?? 0) > EPS;
      const hasGastos = Math.abs(gastos?.valuesByMonth[m] ?? 0) > EPS;
      return hasIngreso || hasGastos;
    });
    return idx.length > 0 ? idx : MONTH_NAMES_ES.map((_, i) => i);
  }, [ingreso, gastos]);

  const visibleMonthKeys = visibleMonthIdx.map((i) => MONTH_NAMES_ES[i]);
  const visibleMonthLabels = visibleMonthIdx.map((i) => MONTH_LABELS[i]);
  const totalByMonth = visibleMonthIdx.map((i) => totalByMonthAll[i]);
  const grandTotal = totalByMonthAll.reduce((a, b) => a + b, 0);
  const colSpanDetail = visibleMonthKeys.length + 1;

  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => ({ value: String(y), label: String(y) }));

  // Clases sticky compartidas: la 1ra columna ("Etiquetas de fila") queda
  // inmovilizada al desplazarse horizontalmente, igual que "Inmovilizar
  // paneles" en Excel. El fondo debe ser sólido (no /60, no /30) para que no
  // se transparente el contenido que pasa por debajo al hacer scroll.
  const stickyCol = 'sticky left-0 z-10';

  const renderDetailRows = (tipoCuenta: TipoCuenta) => {
    const isLoading = loadingDetail[tipoCuenta];
    const rowsDetail = detail[tipoCuenta];
    if (isLoading) {
      return (
        <tr>
          <td colSpan={colSpanDetail} className={['whitespace-nowrap bg-surface px-4 py-3 pl-10 text-sm text-ink-400', stickyCol].join(' ')}>
            Cargando desglose...
          </td>
        </tr>
      );
    }
    if (!rowsDetail || rowsDetail.length === 0) {
      return (
        <tr>
          <td colSpan={colSpanDetail} className={['whitespace-nowrap bg-surface px-4 py-3 pl-10 text-sm text-ink-400', stickyCol].join(' ')}>
            Sin cuentas para este filtro.
          </td>
        </tr>
      );
    }
    return rowsDetail.map((d) => (
      <tr key={d.cuentaCodigo} className="border-b border-ink-50 bg-ink-50">
        <td className={['whitespace-nowrap bg-ink-50 px-4 py-2 pl-10 text-sm text-ink-500', stickyCol].join(' ')}>
          {d.cuentaCodigo} · {d.cuentaDescripcion}
        </td>
        {visibleMonthKeys.map((m) => (
          <td key={m} className="whitespace-nowrap px-4 py-2 text-right text-sm text-ink-500">
            {money(tipoCuenta === 'GASTOS' ? -Math.abs(d.valuesByMonth[m] ?? 0) : d.valuesByMonth[m] ?? 0)}
          </td>
        ))}
        <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium text-ink-600">
          {money(tipoCuenta === 'GASTOS' ? -Math.abs(d.total) : d.total)}
        </td>
      </tr>
    ));
  };

  return (
    // Excepción deliberada al layout móvil-first del resto de la Suite: el
    // director comercial solo usa este módulo en computador, así que "Tabla
    // Resumen" se muestra estilo Excel a todo lo ancho de la pantalla,
    // rompiendo el contenedor centrado (max-w-3xl) de AppShell. El resto de
    // pestañas de "Resultados Financieros" NO se ve afectado.
    <div className="relative left-1/2 -ml-[50vw] w-screen px-4 sm:px-6 lg:px-8">
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-2xl">
        <Select label="Año" options={yearOptions} value={String(anio)} onChange={(e) => setAnio(Number(e.target.value))} />
        <ComparativosMultiSelect label="Tipo Servicio" options={serviceTypeOptions} selected={tiposServicio} onApply={setTiposServicio} />
        <ComparativosMultiSelect label="Cliente" options={clientOptions} selected={clientes} onApply={setClientes} />
      </div>

      <Card padded={false} className="overflow-hidden">
        {/* Contenedor con altura fija: permite scroll vertical y horizontal
            interno "estilo Excel" manteniendo encabezado (thead sticky
            top-0) y columna de etiquetas (sticky left-0) siempre visibles. */}
        <div className="scroll-accent overflow-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                <th className={['sticky left-0 top-0 z-30 whitespace-nowrap border-b border-r border-ink-100 bg-ink-50 px-4 py-3', ].join(' ')}>Etiquetas de fila</th>
                {visibleMonthLabels.map((m) => (
                  <th key={m} className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-ink-50 px-4 py-3 text-right">
                    {m}
                  </th>
                ))}
                <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-ink-50 px-4 py-3 text-right">Total general</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-ink-50">
                <td className={['whitespace-nowrap border-r border-ink-100 bg-surface px-4 py-3 font-medium text-ink-700', stickyCol].join(' ')}>
                  <button type="button" onClick={() => toggleExpand('GASTOS')} className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 text-xs font-bold text-ink-600 hover:bg-ink-100">
                    {expanded.GASTOS ? '−' : '+'}
                  </button>
                  GASTOS
                </td>
                {visibleMonthKeys.map((m) => (
                  <td key={m} className="whitespace-nowrap px-4 py-3 text-right text-critical">
                    {money(-Math.abs(gastos?.valuesByMonth[m] ?? 0))}
                  </td>
                ))}
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-critical">{money(-Math.abs(gastos?.total ?? 0))}</td>
              </tr>
              {expanded.GASTOS && renderDetailRows('GASTOS')}

              <tr className="border-b border-ink-50">
                <td className={['whitespace-nowrap border-r border-ink-100 bg-surface px-4 py-3 font-medium text-ink-700', stickyCol].join(' ')}>
                  <button type="button" onClick={() => toggleExpand('INGRESO')} className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 text-xs font-bold text-ink-600 hover:bg-ink-100">
                    {expanded.INGRESO ? '−' : '+'}
                  </button>
                  INGRESO
                </td>
                {visibleMonthKeys.map((m) => (
                  <td key={m} className="whitespace-nowrap px-4 py-3 text-right text-brand-700">
                    {money(ingreso?.valuesByMonth[m] ?? 0)}
                  </td>
                ))}
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-brand-700">{money(ingreso?.total ?? 0)}</td>
              </tr>
              {expanded.INGRESO && renderDetailRows('INGRESO')}

              <tr className="bg-ink-100">
                <td className={['whitespace-nowrap border-r border-ink-100 bg-ink-100 px-4 py-3 font-bold text-ink-900', stickyCol].join(' ')}>Total general</td>
                {totalByMonth.map((v, i) => (
                  <td key={visibleMonthKeys[i]} className={['whitespace-nowrap px-4 py-3 text-right font-semibold', v >= 0 ? 'text-ink-900' : 'text-critical'].join(' ')}>
                    {money(v)}
                  </td>
                ))}
                <td className={['whitespace-nowrap px-4 py-3 text-right font-bold', grandTotal >= 0 ? 'text-ink-900' : 'text-critical'].join(' ')}>{money(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
